import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { FileText, Check, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { getKnowledgeBaseResponse, upsertKnowledgeBaseResponse } from '@/services/data/knowledgeBase';

interface KnowledgeBaseFormProps {
  userId: string;
  onSaved?: () => void;
}

interface LinkItem {
  id: string;
  url: string;
}

export function KnowledgeBaseForm({ userId, onSaved }: Readonly<KnowledgeBaseFormProps>) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [links, setLinks] = useState<LinkItem[]>([{ id: crypto.randomUUID(), url: '' }]);
  const [cibles, setCibles] = useState<LinkItem[]>([{ id: crypto.randomUUID(), url: '' }]);
  
  const [formData, setFormData] = useState<Record<string, any>>({
    objectif: '',
    echec_si: '',
    domaines_parole: '',
    unique_different: '',
    cible: '',
    cible_se_trompe: '',
    cible_inquiete: '',
    cible_parle_de: '',
    cible_reve: '',
    opinions_tranchees: '',
    ne_laisserai_jamais: '',
    responses: {},
  });

  useEffect(() => {
    const loadData = async () => {
      const result = await getKnowledgeBaseResponse(userId);
      if (result.success && result.data) {
        setFormData(result.data);
        
        // Load links from media_links JSONB column directly
        const mediaLinksData = result.data.media_links as string[] | null;
        if (mediaLinksData && Array.isArray(mediaLinksData) && mediaLinksData.length > 0) {
          setLinks(mediaLinksData.map(url => ({ id: crypto.randomUUID(), url })));
        }
        
        // Load cibles from cible JSONB column directly
        const cibleData = result.data.cible as string[] | null;
        if (cibleData && Array.isArray(cibleData) && cibleData.length > 0) {
          setCibles(cibleData.map(value => ({ id: crypto.randomUUID(), url: value })));
        }
      }
      setIsLoading(false);
    };
    loadData();
  }, [userId]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleLinkChange = (id: string, url: string) => {
    setLinks(prev => {
      return prev.map(link => link.id === id ? { ...link, url } : link);
    });
    setHasChanges(true);
  };

  const handleAddLink = () => {
    setLinks(prev => [...prev, { id: crypto.randomUUID(), url: '' }]);
    setHasChanges(true);
  };

  const handleRemoveLink = (id: string) => {
    if (links.length > 1) {
      setLinks(prev => prev.filter(link => link.id !== id));
    } else {
      setLinks([{ id: crypto.randomUUID(), url: '' }]);
    }
    setHasChanges(true);
  };

  const handleCibleChange = (id: string, value: string) => {
    setCibles(prev => {
      return prev.map(cible => cible.id === id ? { ...cible, url: value } : cible);
    });
    setHasChanges(true);
  };

  const handleAddCible = () => {
    setCibles(prev => [...prev, { id: crypto.randomUUID(), url: '' }]);
    setHasChanges(true);
  };

  const handleRemoveCible = (id: string) => {
    if (cibles.length > 1) {
      setCibles(prev => prev.filter(cible => cible.id !== id));
    } else {
      setCibles([{ id: crypto.randomUUID(), url: '' }]);
    }
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Store links in media_links JSONB column directly
      const validLinks = links.filter(l => l.url.trim()).map(l => l.url.trim());
      // Store cibles in cible JSONB column as array
      const validCibles = cibles.filter(c => c.url.trim()).map(c => c.url.trim());
      const dataToSave = {
        ...formData,
        media_links: validLinks,
        cible: validCibles
      };
      
      const result = await upsertKnowledgeBaseResponse(userId, dataToSave);
      if (result.success) {
        toast.success('Base de connaissance mise à jour');
        setHasChanges(false);
        onSaved?.();
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error saving knowledge base:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="glass-strong border-white/20">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 text-white/50 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-strong border-white/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <FileText className="h-5 w-5 text-white/70" />
          Base de Connaissance
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Objectif */}
        <div className="space-y-2">
          <Label htmlFor="objectif" className="text-white/70 text-sm">
            Quel est ton objectif ?
          </Label>
          <Textarea
            id="objectif"
            value={formData.objectif || ''}
            onChange={(e) => handleChange('objectif', e.target.value)}
            className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40 min-h-[80px]"
            placeholder="Décris ton objectif principal..."
          />
        </div>

        {/* Échec */}
        <div className="space-y-2">
          <Label htmlFor="echec_si" className="text-white/70 text-sm">
            Notre collaboration serait un échec total si...
          </Label>
          <Textarea
            id="echec_si"
            value={formData.echec_si || ''}
            onChange={(e) => handleChange('echec_si', e.target.value)}
            className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40 min-h-[80px]"
            placeholder="Décris ce qui constituerait un échec..."
          />
        </div>

        {/* Domaines de parole */}
        <div className="space-y-2">
          <Label htmlFor="domaines_parole" className="text-white/70 text-sm">
            2 à 4 domaines sur lesquels tu veux prendre la parole (piliers de contenu)
          </Label>
          <Textarea
            id="domaines_parole"
            value={formData.domaines_parole || ''}
            onChange={(e) => handleChange('domaines_parole', e.target.value)}
            className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40 min-h-[100px]"
            placeholder="Ex: 1. Expertise métier&#10;2. Innovation&#10;3. Conseil client&#10;4. Vision du secteur"
          />
        </div>

        {/* Unique/Différent */}
        <div className="space-y-2">
          <Label htmlFor="unique_different" className="text-white/70 text-sm">
            Qu'est-ce qui te rend unique / différent ?
          </Label>
          <Textarea
            id="unique_different"
            value={formData.unique_different || ''}
            onChange={(e) => handleChange('unique_different', e.target.value)}
            className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40 min-h-[80px]"
            placeholder="Décris ce qui te distingue..."
          />
        </div>

        {/* Cible */}
        <div className="space-y-2">
          <Label className="text-white/70 text-sm">
            Qui sont tes cibles ?
          </Label>
          <div className="space-y-2">
            {cibles.map((cible) => (
              <div key={cible.id} className="flex items-center gap-2">
                <Input
                  type="text"
                  value={cible.url}
                  onChange={(e) => handleCibleChange(cible.id, e.target.value)}
                  className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40 flex-1"
                  placeholder="Ex: Dirigeants de PME, Entrepreneurs tech..."
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveCible(cible.id)}
                  className="text-white/40 hover:text-white hover:bg-white/10 shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="ghost"
              onClick={handleAddCible}
              className="w-full glass border border-dashed border-white/20 text-white/60 hover:text-white hover:border-white/40 hover:bg-white/5"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter une cible
            </Button>
          </div>
        </div>

        {/* Sur quoi ta cible se trompe */}
        <div className="space-y-2">
          <Label htmlFor="cible_se_trompe" className="text-white/70 text-sm">
            Sur quoi ta cible se trompe totalement ?
          </Label>
          <Textarea
            id="cible_se_trompe"
            value={formData.cible_se_trompe || ''}
            onChange={(e) => handleChange('cible_se_trompe', e.target.value)}
            className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40 min-h-[80px]"
            placeholder="Quelles idées reçues as-tu identifiées ?"
          />
        </div>

        {/* Ce qui inquiète la cible */}
        <div className="space-y-2">
          <Label htmlFor="cible_inquiete" className="text-white/70 text-sm">
            Qu'est-ce qui inquiète ta cible ?
          </Label>
          <Textarea
            id="cible_inquiete"
            value={formData.cible_inquiete || ''}
            onChange={(e) => handleChange('cible_inquiete', e.target.value)}
            className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40 min-h-[80px]"
            placeholder="Quelles sont leurs préoccupations principales ?"
          />
        </div>

        {/* Ce qui fait parler la cible */}
        <div className="space-y-2">
          <Label htmlFor="cible_parle_de" className="text-white/70 text-sm">
            Qu'est-ce qui fait parler ta cible en ce moment ?
          </Label>
          <Textarea
            id="cible_parle_de"
            value={formData.cible_parle_de || ''}
            onChange={(e) => handleChange('cible_parle_de', e.target.value)}
            className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40 min-h-[80px]"
            placeholder="Quels sont les sujets d'actualité pour ta cible ?"
          />
        </div>

        {/* Ce qui fait rêver la cible */}
        <div className="space-y-2">
          <Label htmlFor="cible_reve" className="text-white/70 text-sm">
            Qu'est-ce qui fait rêver ta cible ?
          </Label>
          <Textarea
            id="cible_reve"
            value={formData.cible_reve || ''}
            onChange={(e) => handleChange('cible_reve', e.target.value)}
            className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40 min-h-[80px]"
            placeholder="Quelles sont leurs aspirations ?"
          />
        </div>

        {/* Opinions tranchées */}
        <div className="space-y-2">
          <Label htmlFor="opinions_tranchees" className="text-white/70 text-sm">
            Tes opinions tranchées
          </Label>
          <Textarea
            id="opinions_tranchees"
            value={formData.opinions_tranchees || ''}
            onChange={(e) => handleChange('opinions_tranchees', e.target.value)}
            className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40 min-h-[80px]"
            placeholder="Quelles sont tes positions fortes ?"
          />
        </div>

        {/* Ne laisserai jamais */}
        <div className="space-y-2">
          <Label htmlFor="ne_laisserai_jamais" className="text-white/70 text-sm">
            "En tant que ... je ne laisserai jamais ... faire ..."
          </Label>
          <Textarea
            id="ne_laisserai_jamais"
            value={formData.ne_laisserai_jamais || ''}
            onChange={(e) => handleChange('ne_laisserai_jamais', e.target.value)}
            className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40 min-h-[80px]"
            placeholder="Complete cette phrase avec ta conviction..."
          />
        </div>

        {/* Podcast/Interview/Article - Multiple links */}
        <div className="space-y-2">
          <Label className="text-white/70 text-sm">
            Podcast / Interview / Article (liens)
          </Label>
          <div className="space-y-2">
            {links.map((link) => (
              <div key={link.id} className="flex items-center gap-2">
                <Input
                  type="url"
                  value={link.url}
                  onChange={(e) => handleLinkChange(link.id, e.target.value)}
                  className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40 flex-1"
                  placeholder="https://..."
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveLink(link.id)}
                  className="text-white/40 hover:text-white hover:bg-white/10 shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="ghost"
              onClick={handleAddLink}
              className="w-full glass border border-dashed border-white/20 text-white/60 hover:text-white hover:border-white/40 hover:bg-white/5"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un lien
            </Button>
          </div>
        </div>

        {/* Bouton d'enregistrement */}
        {hasChanges && (
          <div className="pt-2">
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full bg-warning/20 border border-warning/30 text-white hover:bg-warning/30 active:scale-[0.98] transition-all backdrop-blur-sm disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Enregistrer les réponses
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
